import type { PropsWithChildren } from 'react';

import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { S } from './styles';
import { useTileBaseContext } from '~/components/shared/recommendations/tile/TileBaseContext';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { useMembersOnlyPrices } from '~/utilities/hooks/useMembersOnlyPrices';

interface TilePriceDoubleStrikethroughProps {
  discountedColor?: string;
}

export const TilePriceStrikethrough = ({
  discountedColor,
}: PropsWithChildren<TilePriceDoubleStrikethroughProps>) => {
  const { priceFormatter } = useStaticContext();
  const { singleProduct: product } = useTileBaseContext();
  const { tileType } = useTileContext();
  const theme = useTheme();

  const { priceFormatted: basePrice } = useMembersOnlyPrices({
    price: product.priceValue,
    personalisedPrice: product.personalisedPriceValue,
  });

  const [fromPrice, intermediaryPrice] = useMemo(
    () => [
      product?.fromPriceValue && priceFormatter(product.fromPriceValue),
      product?.intermediaryPriceValue && priceFormatter(product.intermediaryPriceValue),
    ],
    [product, priceFormatter]
  );

  const hasFromPrice = !!fromPrice && fromPrice !== basePrice;
  const hasIntermediaryPrice = !!intermediaryPrice && intermediaryPrice !== basePrice;
  const isDiscounted = hasFromPrice || hasIntermediaryPrice;
  const discountedPriceColor = discountedColor || theme.colors.content.sale;

  return (
    <S.PriceArea>
      {hasFromPrice && (
        <S.Price
          variant="subtitle"
          decoration="line-through"
          component="p"
          alignment="center"
          testId="product-tile-before-price"
          data-cs-capture=""
          className="price-textMedium"
        >
          {fromPrice}
        </S.Price>
      )}

      {tileType !== 'ctl' && hasIntermediaryPrice && (
        <S.Price
          variant="subtitle"
          decoration="line-through"
          component="p"
          alignment="center"
          testId="product-tile-intermediary-price"
          data-cs-capture=""
          className="price-textMedium"
        >
          {intermediaryPrice}
        </S.Price>
      )}

      <S.Price
        variant="h4"
        component="p"
        alignment="center"
        color={isDiscounted ? discountedPriceColor : theme.colors.content.primary}
        testId="product-tile-price"
        className={isDiscounted ? 'price-textBold' : ''}
        data-cs-capture=""
      >
        {basePrice}
      </S.Price>
    </S.PriceArea>
  );
};
